import { useEffect, useMemo, useState } from 'react'
import posthog from 'posthog-js'
import { useParams } from 'react-router-dom'
import { InfiniteData } from 'react-query'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IoClose, IoOptionsOutline, IoSearch } from 'react-icons/io5'
import DotsLoading from 'components/loadings/dotsLoading'
import CustomBottomSheet from 'components/bottomSheet'
import SortPill from 'components/sortPill'
import { TFundTransactionType } from 'services/fund'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'
import Item, { transactionKey } from './item'
import TransactionDetails from './item/transactionDetails'
import { groupByDay } from './days'
import { IFundMemberFilter } from '../memberFilter'
import useFocusTransaction from './useFocusTransaction'

// Long enough that a name being typed is one request, short enough that the
// list answers before the manager wonders whether it heard them.
const SEARCH_DEBOUNCE_MS = 450

/**
 * «نوع تراکنش». The pills carry the very words the rows carry — a filter named
 * «حق عضویت» over rows that say «واریز حق عضویت» makes the reader check that
 * they are the same thing.
 *
 * `cnp,cnpr` is one pill: a membership fee paid online and one a manager typed
 * in are two types on the server and one thing, «واریز حق عضویت», on screen.
 * The two هبه are two pills because they are two things to a fund's books —
 * one covers expenses, the other is capital.
 */
const TYPE_FILTER_GROUPS: {
    title: string
    all: { key: TFundTransactionType; label: string }
    types: { key: TFundTransactionType; label: string }[]
}[] = [
    {
        title: 'واریز',
        all: { key: 'cashin', label: 'همهٔ واریزها' },
        types: [
            { key: 'cnp,cnpr', label: MoneyPoolTransactionTypes.cnp },
            { key: 'cnc', label: MoneyPoolTransactionTypes.cnc },
            { key: 'cnr', label: MoneyPoolTransactionTypes.cnr },
            { key: 'cnhe', label: MoneyPoolTransactionTypes.cnhe },
            { key: 'cnhc', label: MoneyPoolTransactionTypes.cnhc },
        ],
    },
    {
        title: 'برداشت',
        all: { key: 'cashout', label: 'همهٔ برداشت‌ها' },
        types: [
            { key: 'ctc', label: MoneyPoolTransactionTypes.ctc },
            { key: 'ctl', label: MoneyPoolTransactionTypes.ctl },
        ],
    },
]

// How the chosen filter reads after «فقط». A whole side drops its «همهٔ»:
// «فقط همهٔ برداشت‌ها» is two quantifiers arguing.
const TYPE_FILTER_LABELS: Record<string, string> = {
    ...Object.fromEntries(
        TYPE_FILTER_GROUPS.flatMap((group) => group.types).map((option) => [
            option.key,
            option.label,
        ])
    ),
    cashin: 'واریزها',
    cashout: 'برداشت‌ها',
}

interface ITransactionProps {
    data: InfiniteData<IMoneyPoolTransaction> | undefined
    isLoading: boolean
    isManager: boolean
    /**
     * Every tab's list stays mounted and each listens to the same window
     * scroll, so without this, scrolling the members would page this list too.
     */
    isActive: boolean
    fetchNextPage: () => void
    hasNextPage: boolean
    isFetchingNextPage?: boolean
    /** `?tx=in-123`: scroll to this row and light it up (useFocusTransaction) */
    focusKey?: string | null
    /** what is typed in the search box */
    searchTrans: string
    onSearchChange: (text: string) => void
    /** what the list on screen was actually asked for */
    appliedSearch: string
    setSearchtransaction: React.Dispatch<React.SetStateAction<string>>
    /** «نوع تراکنش», '' for همه */
    type: TFundTransactionType
    onTypeChange: (next: TFundTransactionType) => void
    /** the #hashtag currently filtering the list, '' when there is none */
    hashtagFilter: string
    onHashtagClick: (hashtag: string) => void
    onClearHashtag: () => void
    /** the one member the list is narrowed to, null for everybody */
    memberFilter?: IFundMemberFilter | null
    onClearMemberFilter?: () => void
}
export default function Transactions({
    data,
    isLoading,
    isManager,
    isActive,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage = false,
    focusKey = null,
    searchTrans,
    onSearchChange,
    appliedSearch,
    setSearchtransaction,
    type,
    onTypeChange,
    hashtagFilter,
    onHashtagClick,
    onClearHashtag,
    memberFilter = null,
    onClearMemberFilter,
}: ITransactionProps) {
    const { id } = useParams()
    const [openKey, setOpenKey] = useState<string>('')
    const [filterOpen, setFilterOpen] = useState(false)

    const toggleFilter = () => {
        if (!filterOpen) {
            posthog.capture('fund_transactions_filter_opened', {
                moneypool_id: Number(id),
                type: type || null,
            })
        }
        setFilterOpen(!filterOpen)
    }

    const chooseType = (next: TFundTransactionType) => {
        // One choice out of one group, so choosing is also the end of the
        // errand: the panel folds away and the list it made is what is left.
        setFilterOpen(false)
        if (next === type) return
        posthog.capture('fund_transactions_type_filtered', {
            moneypool_id: Number(id),
            type: next || null,
            has_search: appliedSearch !== '',
            has_member_filter: !!memberFilter,
        })
        onTypeChange(next)
    }

    // Pages are fetched at different moments, so a transaction recorded while
    // the manager scrolls pushes a row from the end of one page onto the start
    // of the next. Seen twice, it is shown once.
    const rows = useMemo(() => {
        if (!data) return undefined
        const seen = new Set<string>()
        return data.pages
            .flatMap((page) => page?.results?.result ?? [])
            .filter((row) => {
                const key = transactionKey(row)
                if (seen.has(key)) return false
                seen.add(key)
                return true
            })
    }, [data])

    const rowKeys = useMemo(() => (rows ?? []).map(transactionKey), [rows])
    const { highlighted } = useFocusTransaction({
        fundId: id,
        focusKey,
        rowKeys,
        loadedPages: data?.pages.length ?? 0,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        enabled: isActive && !!data,
    })

    const days = useMemo(() => (rows ? groupByDay(rows) : []), [rows])

    // Read from the live list rather than remembered at tap time: the sheet
    // stays open across refetches, and an edited description has to reach it.
    const openItem = useMemo(
        () =>
            openKey && rows
                ? rows.find((row) => transactionKey(row) === openKey)
                : undefined,
        [openKey, rows]
    )

    useEffect(() => {
        const handle = setTimeout(
            () => setSearchtransaction(searchTrans.trim()),
            SEARCH_DEBOUNCE_MS
        )
        return () => clearTimeout(handle)
    }, [searchTrans, setSearchtransaction])

    // What the hashtag search actually returned. Fired here rather than next to
    // the tap, because the tap only sets a `q=` — whether it found anything is
    // known one round trip later, and «تراکنشی با این هشتگ پیدا نشد» is the
    // outcome worth counting.
    useEffect(() => {
        if (hashtagFilter === '' || !rows) return
        posthog.capture('transaction_hashtag_search_results', {
            moneypool_id: Number(id),
            result_count: rows.length,
            is_empty: rows.length === 0,
        })
        // Keyed on the filter and the size of its answer, not on the array
        // identity, which changes on every background refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hashtagFilter, rows?.length, id])

    // The same for a typed search. The text never leaves the device — it is a
    // member's name or phone number — only how long it was and what it found.
    const firstPageLength = data?.pages?.[0]?.results?.result?.length
    useEffect(() => {
        if (appliedSearch === '' || hashtagFilter !== '') return
        if (firstPageLength === undefined) return
        posthog.capture('fund_transactions_searched', {
            moneypool_id: Number(id),
            query_length: appliedSearch.length,
            result_count: firstPageLength,
            is_empty: firstPageLength === 0,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearch, firstPageLength, id])

    const loadMore = () => {
        // Whether anyone reads past the first twenty is the question the tab
        // could not answer before it paged at all.
        posthog.capture('fund_transactions_page_loaded', {
            moneypool_id: Number(id),
            page: (data?.pages.length ?? 0) + 1,
            is_search: appliedSearch !== '',
            type: type || null,
            has_member_filter: !!memberFilter,
        })
        fetchNextPage()
    }

    const isFiltered = appliedSearch !== ''
    // What the whole search / filter found, not what has been scrolled into
    // view. Absent from a server that predates it, and then simply not shown.
    const foundCount = data?.pages?.[0]?.results?.pagination?.count

    return (
        <div className="min-h-screen bg-white pb-6">
            {/* The one member the list is narrowed to. First on the tab, above
                the search: it changes what every row below is, and it is what
                someone arriving from a کارنامه came for. */}
            {memberFilter && (
                <div className="mx-4 mt-3 flex items-center justify-between gap-2 rounded-[10px] bg-blue-6 px-3 py-2">
                    <p className="mb-0 min-w-0 truncate text-sm text-blue-5">
                        فقط تراکنش‌های{' '}
                        <span className="font-bold">{memberFilter.label}</span>
                    </p>
                    {onClearMemberFilter && (
                        <button
                            type="button"
                            onClick={onClearMemberFilter}
                            aria-label="نمایش تراکنش‌های همهٔ اعضا"
                            className="shrink-0 text-sm font-bold text-blue-5"
                        >
                            حذف فیلتر
                        </button>
                    )}
                </div>
            )}

            <div className="flex items-center gap-2 px-4 pt-3">
                <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[10px] bg-sunken px-3 ring-1 ring-transparent transition-shadow focus-within:ring-green-1">
                    <IoSearch
                        aria-hidden
                        className="h-[18px] w-[18px] shrink-0 text-text-secondary"
                    />
                    <input
                        type="search"
                        enterKeyHint="search"
                        aria-label="جستجوی تراکنش‌ها"
                        className="min-w-0 flex-1 text-ellipsis bg-transparent text-sm text-black outline-none placeholder:text-[13px] placeholder:text-text-muted [&::-webkit-search-cancel-button]:hidden"
                        // «#هشتگ» is in here because nothing else on the screen
                        // says a description's hashtag can be searched for. It
                        // sits before «توضیحات» so that on a 320px phone, where
                        // the end of the line is cut, the hint is not what goes.
                        placeholder="نام، شماره همراه، #هشتگ یا توضیحات"
                        value={searchTrans}
                        onChange={(event) => onSearchChange(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter') return
                            setSearchtransaction(searchTrans.trim())
                            event.currentTarget.blur()
                        }}
                    />
                    {searchTrans !== '' && (
                        <button
                            type="button"
                            aria-label="پاک کردن جستجو"
                            onClick={() =>
                                hashtagFilter !== ''
                                    ? onClearHashtag()
                                    : onSearchChange('')
                            }
                            className="-ml-1 shrink-0 p-1 text-text-secondary"
                        >
                            <IoClose className="h-4 w-4" />
                        </button>
                    )}
                </label>
                <button
                    type="button"
                    onClick={toggleFilter}
                    aria-label="فیلتر نوع تراکنش"
                    aria-expanded={filterOpen}
                    aria-controls="fund-transactions-filter"
                    className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] transition-colors ${
                        type !== '' || filterOpen
                            ? 'bg-brand-soft text-brand-deep'
                            : 'bg-sunken text-text-secondary'
                    }`}
                >
                    <IoOptionsOutline className="h-5 w-5" />
                    {type !== '' && (
                        <span
                            aria-hidden
                            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-green-1 ring-2 ring-white"
                        />
                    )}
                </button>
            </div>

            {filterOpen && (
                <div
                    id="fund-transactions-filter"
                    role="radiogroup"
                    aria-label="نوع تراکنش"
                    className="mx-4 mt-2 rounded-2xl border border-stroke bg-white p-3"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <SortPill
                            checked={type === ''}
                            onChange={() => chooseType('')}
                            label="همه"
                            aria="همهٔ تراکنش‌ها"
                        />
                    </div>
                    {TYPE_FILTER_GROUPS.map((group) => (
                        <div key={group.title}>
                            <p className="mb-1.5 mt-3 text-[11px] text-text-muted">
                                {group.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                {[group.all, ...group.types].map((option) => (
                                    <SortPill
                                        key={option.key}
                                        checked={type === option.key}
                                        onChange={() => chooseType(option.key)}
                                        label={option.label}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* What the list is narrowed to stays on screen once the panel is
                closed — a list that holds a filter nobody can see reads as
                «تراکنش‌هایم کم شده». */}
            {!filterOpen && (type !== '' || isFiltered || !!memberFilter) && (
                <div className="mx-4 mt-2 flex items-center justify-between gap-2 text-xs leading-5">
                    <p className="mb-0 min-w-0 truncate text-text-secondary">
                        {type !== '' && (
                            <>
                                فقط{' '}
                                <span className="font-bold text-black">
                                    {TYPE_FILTER_LABELS[type]}
                                </span>
                            </>
                        )}
                        {foundCount !== undefined && (
                            <>
                                {type !== '' ? '، ' : ''}
                                {foundCount.toLocaleString()} تراکنش
                                {type === '' ? ' پیدا شد' : ''}
                            </>
                        )}
                    </p>
                    {type !== '' && (
                        <button
                            type="button"
                            onClick={() => chooseType('')}
                            className="shrink-0 font-bold text-brand-deep"
                        >
                            حذف فیلتر
                        </button>
                    )}
                </div>
            )}

            {/* The active hashtag filter. Shown above the rows rather than as
                a toast, because a list that silently holds a filter is a list
                people read as "my transactions disappeared". */}
            {hashtagFilter !== '' && (
                <div className="mx-4 mt-2 flex items-center justify-between gap-2 rounded-[10px] bg-blue-6 px-3 py-2">
                    <p className="truncate text-sm text-blue-5">
                        تراکنش‌های{' '}
                        <span className="font-bold">{hashtagFilter}</span>
                    </p>
                    <button
                        type="button"
                        onClick={onClearHashtag}
                        className="shrink-0 text-sm font-bold text-blue-5"
                    >
                        حذف فیلتر
                    </button>
                </div>
            )}

            {isLoading || !rows ? (
                <DotsLoading />
            ) : rows.length === 0 ? (
                <p className="mt-10 px-4 text-center text-sm leading-6 text-text-secondary">
                    {hashtagFilter !== ''
                        ? 'تراکنشی با این هشتگ پیدا نشد'
                        : isFiltered && type !== ''
                        ? 'تراکنشی از این نوع با این جستجو پیدا نشد.'
                        : isFiltered
                        ? 'تراکنشی با این جستجو پیدا نشد. نام عضو، شماره همراه یا بخشی از توضیحات را امتحان کنید.'
                        : type !== ''
                        ? 'تراکنشی از این نوع ثبت نشده است.'
                        : memberFilter
                        ? `تراکنشی برای ${memberFilter.label} پیدا نشد.`
                        : 'تراکنشی وجود ندارد'}
                </p>
            ) : (
                <InfiniteScroll
                    next={loadMore}
                    hasMore={isActive && hasNextPage}
                    dataLength={rows.length}
                    loader={<DotsLoading />}
                    // The library's wrapper is `overflow: auto`, which would
                    // make IT the scroll container the sticky day headings
                    // stick to — and it never scrolls, so they never would.
                    style={{ overflow: 'visible' }}
                >
                    {days.map((day, index) => (
                        <section
                            key={`${day.key}-${index}`}
                            aria-label={`${day.title} ${day.date}`}
                        >
                            <h3 className="sticky top-[44px] z-[5] m-0 flex items-baseline gap-1.5 bg-white px-5 pb-1.5 pt-4 text-xs">
                                <span className="font-bold text-black">
                                    {day.title}
                                </span>
                                <span className="text-text-secondary">
                                    {day.date}
                                </span>
                            </h3>
                            <div className="mx-4 divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB] bg-white">
                                {day.rows.map((item) => (
                                    <Item
                                        item={item}
                                        key={transactionKey(item)}
                                        highlighted={
                                            highlighted === transactionKey(item)
                                        }
                                        isManager={isManager}
                                        onOpen={() =>
                                            setOpenKey(transactionKey(item))
                                        }
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </InfiniteScroll>
            )}

            <CustomBottomSheet
                onDismiss={() => setOpenKey('')}
                isOpen={!!openItem}
            >
                {openItem && (
                    <TransactionDetails
                        // One sheet serves every row, and it keeps its own
                        // draft of the description: keyed, so opening another
                        // transaction does not inherit the last one's edit.
                        key={openKey}
                        item={openItem}
                        isManager={isManager}
                        setIsOpen={() => setOpenKey('')}
                        onHashtagClick={(hashtag) => {
                            // Close first: the filtered list renders behind
                            // this sheet, and leaving it open hides the result
                            // of the tap that caused it.
                            setOpenKey('')
                            onHashtagClick(hashtag)
                        }}
                    />
                )}
            </CustomBottomSheet>
        </div>
    )
}
